import React from "react";

import { Input } from "../../../components/ui/input/SimpleInput";
import SelectDropdownV2 from "../../../components/ui/selectdropdown/v2";

import { OptionNameValuePair } from "../../../lib/interfaces/types";

import {
  ASSET_CLASS_LABELS,
  ASSET_TYPE_LABELS,
  AssetClassName,
  AssetFields,
  AssetType,
  FINANCIAL_ASSET_TYPES,
  LAND_PARCEL_TYPES,
  MOVABLE_ASSET_TYPES,
  REAL_ESTATE_TYPES,
  WORKING_CAPITAL_ASSET_TYPES,
} from "../../../api/assets";

interface AssetFormProps {
  values: Partial<AssetFields>;
  onValuesChange: (values: Partial<AssetFields>) => void;
  isEditMode?: boolean;
}

const ASSET_CLASS_OPTIONS: OptionNameValuePair<AssetClassName>[] =
  Object.entries(ASSET_CLASS_LABELS).map(([value, name]) => ({
    name,
    value: value as AssetClassName,
  }));

const ASSET_TYPE_MAP: Partial<Record<AssetClassName, AssetType[]>> = {
  [AssetClassName.FINANCIAL]: FINANCIAL_ASSET_TYPES,
  [AssetClassName.REAL_ESTATE]: REAL_ESTATE_TYPES,
  [AssetClassName.LAND_PARCEL]: LAND_PARCEL_TYPES,
  [AssetClassName.MOVABLE]: MOVABLE_ASSET_TYPES,
  [AssetClassName.WORKING_CAPITAL]: WORKING_CAPITAL_ASSET_TYPES,
};

const SECTION_LABEL_CLASS = "text-xs font-medium leading-none text-[#737373]";

const FIELD_LABEL_CLASS ="text-sm font-medium leading-none text-[#0A0A0A]";

const INPUT_CLASS ="h-9 px-3 py-0 rounded-[8px] border-[#E5E5E5] bg-white shadow-[0_1px_2px_rgba(0,0,0,0.05)] placeholder:text-[#737373]";

const AssetForm: React.FC<AssetFormProps> = ({
  values,
  onValuesChange,
  isEditMode = false,
}) => {
  const assetTypeOptions = React.useMemo<
    OptionNameValuePair<AssetType>[]
  >(
    () =>
      (ASSET_TYPE_MAP[values.asset_class as AssetClassName] ?? []).map(
        type => ({
          name: ASSET_TYPE_LABELS[type],
          value: type,
        }),
      ),
    [values.asset_class],
  );

  const isAssetTypeDisabled =!values.asset_class || isEditMode;

  const isAssetClassDisabled = isEditMode;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3">
        <span className={SECTION_LABEL_CLASS}>
          Properties
        </span>

        <div className="flex items-start gap-4">
          <div className="flex flex-1 flex-col gap-2">
            <label className={FIELD_LABEL_CLASS}>
              Asset Class
              <span className="text-[#DC2626]">*</span>
            </label>

            <SelectDropdownV2
              name="asset class"
              options={ASSET_CLASS_OPTIONS}
              selectedOption={ASSET_CLASS_OPTIONS.find(
                option =>
                  option.value === values.asset_class,
              )}
              onSelectionChange={option =>
                onValuesChange({
                  ...values,
                  asset_class: option?.value,
                  asset_type: undefined,
                })
              }
              enableSearch={false}
              disabled={isAssetClassDisabled}
              containerClassName={`rounded-[8px] border border-[#E5E5E5] shadow-[0_1px_2px_rgba(0,0,0,0.05)] ${
                isAssetClassDisabled ? "cursor-not-allowed" : "bg-white"
              }`}
              labelClassName={`h-9 px-3 py-2 ${
                isAssetClassDisabled ? "cursor-not-allowed" : ""
              }`}
              titleClassName={
                isAssetClassDisabled
                  ? "text-[#737373] font-normal"
                  : undefined
              }
            />
          </div>

          <div className="flex flex-1 flex-col gap-2">
            <label className={FIELD_LABEL_CLASS}>
              Asset Type
              <span className="text-[#DC2626]">*</span>
            </label>

            <SelectDropdownV2
              name="asset type"
              options={assetTypeOptions}
              selectedOption={assetTypeOptions.find(
                option =>
                  option.value === values.asset_type,
              )}
              onSelectionChange={option =>
                onValuesChange({
                  ...values,
                  asset_type: option?.value,
                })
              }
              enableSearch={false}
              disabled={isAssetTypeDisabled}
              containerClassName={`rounded-[8px] border border-[#E5E5E5] shadow-[0_1px_2px_rgba(0,0,0,0.05)] ${
                isAssetTypeDisabled ? "cursor-not-allowed" : "bg-white"
              }`}

              labelClassName={`h-9 px-3 py-2 ${
                isAssetTypeDisabled ? "cursor-not-allowed" : ""
              }`}
              titleClassName={
                isAssetClassDisabled
                  ? "text-[#737373] font-normal"
                  : undefined
              }
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <span className={SECTION_LABEL_CLASS}>
          Identifiers
        </span>

        <div className="flex items-start gap-4">
          <div className="flex flex-1 flex-col gap-2">
            <label className={FIELD_LABEL_CLASS}>
              ID
              <span className="text-[#DC2626]">*</span>
            </label>

            <Input
              type="text"
              placeholder="Enter ID"
              value={values.ext_asset_id ?? ""}
              onChange={e =>
                onValuesChange({
                  ...values,
                  ext_asset_id: e.target.value,
                })
              }
              className={INPUT_CLASS}
            />
          </div>

          <div className="flex flex-1 flex-col gap-2">
            <label className={FIELD_LABEL_CLASS}>
              Name
            </label>

            <Input
              type="text"
              placeholder="Enter Name"
              value={values.asset_name ?? ""}
              onChange={e =>
                onValuesChange({
                  ...values,
                  asset_name: e.target.value,
                })
              }
              className={INPUT_CLASS}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetForm;